<script setup lang="ts">
import { ref } from 'vue'
import SkillCollection from './components/SkillCollection.vue'
import { useUserStore } from '@/stores/modules/user'
import { soulList, soulClass } from './data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { SkillItemType } from '@/types/skill'
import { getSkillName } from '@/core/skill'
import type { SecondAwakenRes } from '@/types/peak'
import { getEquipName } from '@/utils/equip'
import type { EquipItemType } from '@/types/equip'
import SkillInfo from '../item/components/SkillInfo.vue'
import EquipInfo from '../item/components/EquipInfo.vue'
import SoulInfo from './components/SoulInfo.vue'
import { equipCollection } from '@/core/equip'
import SkillBless from './components/SkillBless.vue'

const userStore = useUserStore()
const curTab = ref('enlightenment')

const isChange = ref(false)
function handleChangeSoul() {
  if (userStore.diamond < 2000) return ElMessage.warning('转换战魂需 2000钻石')
  isChange.value = true
}

const showInitSkill = ref(false)
const initSkillList = ref<SkillItemType[]>([])
const initItemList = ref<EquipItemType[]>([])
function handleChoiceSoul(soulType: number, name: string) {
  ElMessageBox.confirm(
    `<p class="affix">确定选择<span class="${soulClass[soulType]}">【${name}】</span>作为您的战魂吗？${
      isChange.value ? '（本次选择花费 <span class="diamond">2000</span>钻石）' : ''
    }</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<SecondAwakenRes>({ type: 'secondAwakening', data: { soulType } })
      const { skillList, itemList, userAttr } = res
      if (userStore.soulType <= 3) {
        userStore.skillList.push(...skillList)
        userStore.itemList.push(...itemList)
        initSkillList.value = skillList
        initItemList.value = itemList.map((item) => {
          const { equipId } = item
          const { name, type, attrs } = equipCollection[equipId]
          return { ...item, name, type, attrs }
        })
        showInitSkill.value = true
      }
      userStore.soulType = soulType
      userStore.userAttr = userAttr
      if (isChange.value) {
        userStore.diamond -= 2000
        isChange.value = false
        return ElMessage.success('转换战魂成功')
      }
    })
    .catch(() => {})
}
</script>

<template>
  <div class="second-awaken-page page">
    <div class="choice-soul" v-if="userStore.soulType <= 3 || isChange || showInitSkill">
      <h2>请选择您的战魂</h2>
      <div class="intro affix">
        <p>1. 选择您喜欢的战魂进行<span class="second-awaken">二次觉醒</span>，并获得元素伤害加成</p>
        <p>2. 二次觉醒后，将大幅改变机制：</p>
        <p style="text-indent: 2em">a. 不再生效装备的<span class="info">附魔属性</span></p>
        <p style="text-indent: 2em">
          b. <span class="awaken">符石</span>将在后续的更新中支持置换为<span class="second-awaken">共鸣符石</span>，并同步调整副本难度
        </p>
        <p style="text-indent: 2em">c. <span class="relic">圣物</span>的元素伤害加成改变为<span class="grow">独立元素加成</span></p>
        <p style="text-indent: 2em">d. 解锁<span class="info">技能系统</span></p>
        <p style="text-indent: 2em">e. 解锁<span class="grow">5</span>个全新的部位：👖、🧤、📿、🎗️、🥇</p>
        <p style="text-indent: 2em">f. 修正<span class="myth">菊一文字</span>特效生效顺序错误的问题</p>
        <p>3. 二次觉醒后，副本的调整如下：</p>
        <p style="text-indent: 2em">
          a. 不再获得以下副本的参与奖励：<span class="antiquity">不朽之塔</span>、<span class="pet">魔物巢穴</span>、<span class="wild">大荒野</span
          >、<span class="peerless">虚空裂隙</span>、<span class="hp">魔龙山巅</span>
        </p>
        <p style="text-indent: 2em">
          b. 支持以下副本的扫荡：<span class="awaken">符石尖塔</span>、<span class="awaken">符石神殿</span>、<span class="darkGold">无尽之塔</span
          >（最高可获得<span class="grow">50</span>层的奖励)、<span class="relic">圣物遗迹</span>（探寻后可扫荡）、<span class="myth">鎏金神殿</span>
        </p>
        <p style="text-indent: 2em">c. 默认不显示已关闭的副本，您可在设置中开启显示</p>
        <p>
          3. 首次<span class="second-awaken">二次觉醒</span>将获得<span class="grow">20</span>件D级的随机<span class="supreme">至臻</span
          >装备及对应元素的技能书
        </p>
        <p>4. 选择战魂后可花费<span class="diamond">钻石</span>转换为其他战魂</p>
      </div>
      <div class="soul-list">
        <CommonBtn
          v-for="item of soulList.filter((item) => item.id !== userStore.soulType)"
          :type="item.type"
          style="text-align: left; width: 100%"
          @done="handleChoiceSoul(item.id, item.name)"
        >
          <div class="soul-item">
            <h4>{{ item.name }}</h4>
            <p>加成：{{ item.attrs }}</p>
          </div>
        </CommonBtn>
      </div>
      <CommonBtn type="warning" @click="isChange = false" style="width: 100%; margin-top: 30px" v-show="isChange">取消转换</CommonBtn>

      <el-dialog v-model="showInitSkill" width="100%" style="max-width: 500px">
        <h2 class="affix">
          <span class="grow">觉醒成功，获得以下奖励</span>
        </h2>
        <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of initSkillList" :key="item.id">
              <el-popover placement="bottom" width="300px" trigger="click">
                <template #reference>
                  <CommonBtn size="small" style="margin-bottom: 5px">
                    <span :class="item.quality" v-html="getSkillName(item)"> </span>
                  </CommonBtn>
                </template>
                <SkillInfo :skill-info="item" :show-action="false" />
              </el-popover>
            </el-col>
            <el-col :span="12" v-for="item of initItemList" :key="item.id">
              <el-popover placement="bottom" width="300px" trigger="click">
                <template #reference>
                  <CommonBtn size="small" style="margin-bottom: 5px">
                    <span v-html="getEquipName(item, item.type)"> </span>
                  </CommonBtn>
                </template>
                <EquipInfo :equip-info="item" :show-action="false" />
              </el-popover>
            </el-col>
          </el-row>
        </div>
        <template #footer>
          <p style="text-align: center">
            <el-button plain @click="showInitSkill = false" size="small">关闭</el-button>
          </p>
        </template>
      </el-dialog>
    </div>
    <div class="awaken-palace" v-else>
      <h2>二觉殿堂</h2>
      <el-tabs v-model="curTab" stretch>
        <el-tab-pane label="启迪" name="enlightenment" class="pane" lazy>
          <SkillBless class="pane-item" />
        </el-tab-pane>
        <el-tab-pane label="图鉴" name="skill" class="pane" lazy>
          <SkillCollection class="pane-item" />
        </el-tab-pane>
        <el-tab-pane label="战魂" name="warSoul" class="pane" lazy>
          <SoulInfo class="pane-item" @changeSoul="handleChangeSoul" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.second-awaken-page {
  padding-top: 20px;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  overflow: hidden;
  .intro {
    max-height: 30vh;
    overflow: auto;
    margin-bottom: 20px;
    p {
      font-size: 14px;
      margin: 8px 0;
    }
  }
  .el-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    :deep() {
      .el-tabs__content {
        flex: 1;
        .pane {
          height: 100%;
          .pane-item {
            height: 100%;
            overflow: hidden;
            display: flex;
          }
        }
      }
    }
  }
  .choice-soul {
    height: 100%;
    h2 {
      margin-top: 0;
    }
    .el-button {
      > span {
        display: flex;
        flex-flow: column nowrap;
      }
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
    .soul-item {
      h4 {
        text-align: left;
        margin: 0 0 10px 0;
      }
      p {
        font-size: 12px;
        margin: 4px 0;
      }
    }
  }
  .awaken-palace {
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
    overflow: hidden;
  }
  .btn-wrap > div {
    align-items: flex-start;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
